import { useCallback, useMemo, useRef, MouseEvent } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { NotificationsPortalContext } from '@nx-frontend/shared/types';
import { NotificationPanelFilterOption } from './notification-panel-filter-option';
import { NotificationPanelFilterOptionButton } from './notification-panel-filter-option-button';
import {
  NotificationPanelFilterWorkspace,
  NotificationPanelFilterWorkspaceIcon,
} from './notification-panel-filter-workspace-icon';
import {
  $notificationPanelFilter,
  $notificationPanelWorkspaceId,
  setNotificationPanelFilterValuesEvent,
} from './store';

const transitionClasses: Record<string, string> = {
  entering: 'opacity-1 transform translate-x-0',
  entered: 'opacity-1 transform translate-x-0',
  exiting: 'opacity-0 transform translate-x-0',
  exited: 'opacity-0 transform translate-x-[-20px]',
};

interface NotificationPanelFilterByPlaceProps {
  portalContext: NotificationsPortalContext;
  workspace?: NotificationPanelFilterWorkspace;
}

export function NotificationPanelFilterByPlace(props: NotificationPanelFilterByPlaceProps) {
  const t = useTranslations('notification_panel');

  const { portalContext, workspace } = props;
  const [notificationPanelFilter, notificationPanelWorkspaceId] = useUnit([
    $notificationPanelFilter,
    $notificationPanelWorkspaceId,
  ]);
  const [setNotificationPanelFilterValues] = useUnit([setNotificationPanelFilterValuesEvent]);
  const { workspaceId, portalId } = notificationPanelFilter;

  const nodeRef = useRef(null);

  const placeValues = useMemo(() => {
    return {
      workspaceId: notificationPanelWorkspaceId,
      portalId: portalContext.portalId,
    };
  }, [portalContext, notificationPanelWorkspaceId]);

  const handlePlaceSelect = useCallback(
    (filterProp: 'workspaceId' | 'portalId', only = false) => {
      const value = notificationPanelFilter[filterProp];
      const nullableProps = Object.keys(placeValues).reduce((acc, key) => ({ ...acc, [key]: null }), {});
      setNotificationPanelFilterValues({
        ...(only ? nullableProps : {}),
        [filterProp]: value && !only ? null : placeValues[filterProp],
      });
    },
    [notificationPanelFilter, placeValues, setNotificationPanelFilterValues]
  );

  const handleClearAll = useCallback(() => {
    setNotificationPanelFilterValues({
      workspaceId: null,
      portalId: null,
    });
  }, [setNotificationPanelFilterValues]);

  const isPortalDisabled = useMemo(() => {
    return !portalContext?.portalId || portalContext.isPortalAccessDenied;
  }, [portalContext]);

  return (
    <Transition in={notificationPanelFilter.showFilterByPlace} nodeRef={nodeRef} timeout={600} unmountOnExit={true}>
      {(state: TransitionStatus) => (
        <div
          data-cy="notif-panel-filter-by-place"
          ref={nodeRef}
          data-state={state}
          className={classNames(
            'w-[256px] border border-neutral-200 rounded bg-white absolute py-2 shadow-[0_1.49px_11.21px_0_#A3A8AD38] left-full top-0 transition-all',
            transitionClasses[state]
          )}
        >
          {workspace ? (
            <NotificationPanelFilterOption
              onClick={(e: MouseEvent) => e.stopPropagation()}
              title={workspace?.name}
              icon={<NotificationPanelFilterWorkspaceIcon workspace={workspace} />}
              inactive={true}
            />
          ) : null}
          <NotificationPanelFilterOption
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handlePlaceSelect('workspaceId');
            }}
            title={t('notification_panel-internal_space')}
            checked={!!workspaceId}
            size="lg"
          />
          <NotificationPanelFilterOption
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              !isPortalDisabled && handlePlaceSelect('portalId');
            }}
            title={portalContext?.settings?.name || 'Client portal'}
            checked={!!portalId}
            disabled={isPortalDisabled}
            tooltip={
              isPortalDisabled
                ? !portalContext?.portalId
                  ? "Create a portal, and you'll be able to filter notifications based on places"
                  : "You don't have access to the portal"
                : undefined
            }
            size="lg"
          />
          <div className="border-t border-neutral-200 my-1"></div>
          {/*}
          {!isPortalDisabled ? (
            <NotificationPanelFilterOptionButton
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                handlePlaceSelect('portalId', true);
              }}
              title="Select only Portal"
              additionClasses="pl-6 ml-3"
            />
          ) : null}
          <NotificationPanelFilterOptionButton
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              handlePlaceSelect('workspaceId', true);
            }}
            title="Select only Internal"
            additionClasses="pl-6 ml-3"
          />
          */}
          <NotificationPanelFilterOptionButton
            onClick={handleClearAll}
            title={t('notification_panel-clear_all')}
            additionClasses="pl-6 ml-3"
          />
        </div>
      )}
    </Transition>
  );
}
